/** @jsxImportSource @emotion/react */
import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Card } from "primereact/card";
import { Column } from "primereact/column";
import { Toast } from "primereact/toast";
import { Skeleton } from "primereact/skeleton";
import { Accordion, AccordionTab } from "primereact/accordion";
import { Tag } from "primereact/tag";
import {
  Boton,
  Titulo,
  Formulario,
  accessAPI,
  formatFecha,
  Sidebar,
  tituloBase,
  TablaFiltrable
} from "../../../../shared";

const SEARCH_FIELDS = ["instrumento", "marca", "modelo"];

export default function Instrumental() {
  const [loader, setLoader] = useState(true);
  const [instrumental, setInstrumental] = useState([]);
  const [instrumentalNoActivo, setInstrumentalNoActivo] = useState([]);
  const [sideBar, setSideBar] = useState(false);
  const [formLoader, setFormLoader] = useState(false);

  const toast = useRef(null);
  const navigate = useNavigate();

  function crearInstrumento(contenidoCampos, archivo) {
    setFormLoader(true);
    accessAPI(
      "POST",
      "admin/instrumental",
      contenidoCampos,
      (respuesta) => {
        navigate(`/instrumental/${respuesta.id}`);
      },
      (respuesta) => {
        setFormLoader(false);
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: respuesta[0]?.msg || "Error al crear instrumento",
          sticky: true
        });
      },
      archivo
    );
  }

  const crearFormConfig = {
    secciones: [
      {
        nombre: "Nuevo instrumento",
        campos: [
          {
            nombre: "instrumento",
            tipoitemformularioid: 1,
            etiqueta: "Tipo de instrumento",
            requerido: true
          },
          {
            nombre: "marca",
            tipoitemformularioid: 1,
            etiqueta: "Marca",
            requerido: true
          },
          {
            nombre: "modelo",
            tipoitemformularioid: 1,
            etiqueta: "Modelo",
            requerido: true
          },
          {
            nombre: "numeroSerie",
            tipoitemformularioid: 1,
            etiqueta: "Número de serie"
          },
          {
            nombre: "caracteristicas",
            tipoitemformularioid: 11,
            etiqueta: "Características"
          },
          {
            nombre: "frecuenciaCalibracionMeses",
            tipoitemformularioid: 4,
            etiqueta: "Frecuencia de calibración en meses"
          },
          {
            nombre: "instructivo",
            tipoitemformularioid: 10,
            etiqueta: "Archivo instructivo"
          }
        ]
      }
    ],
    botones: [
      {
        etiqueta: "Crear instrumento",
        callback: crearInstrumento,
        validar: true
      }
    ]
  };

  useEffect(() => {
    document.title = "Instrumental";
    accessAPI(
      "GET",
      "admin/instrumental",
      null,
      (respuesta) => {
        setInstrumental(respuesta.instrumental);
        setInstrumentalNoActivo(respuesta.instrumentalNoActivo);
        setLoader(false);
      },
      (respuesta) => {
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: respuesta[0]?.msg || "Error al cargar instrumental",
          sticky: true
        });
        setLoader(false);
      }
    );
    return () => (document.title = tituloBase());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onRowClick = (e) => {
    if (e.originalEvent.metaKey || e.originalEvent.ctrlKey) {
      window.open(`/instrumental/${e.data.id}`, "_blank");
    } else {
      navigate(`/instrumental/${e.data.id}`);
    }
  };

  return (
    <>
      <Toast ref={toast} position="top-center" />

      <Sidebar
        visible={sideBar}
        onHide={() => setSideBar(false)}
        position="right"
        className="w-full lg:w-30rem md:w-30rem"
      >
        <Titulo>Nuevo instrumento</Titulo>
        <Formulario config={crearFormConfig} loader={formLoader} />
      </Sidebar>

      {loader && (
        <div
          css={{
            marginTop: "2em",
            display: "flex",
            flexDirection: "column",
            gap: "0.5rem"
          }}
        >
          <div
            css={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "0.5rem"
            }}
          >
            <Skeleton width="15rem" height="2.5rem" />
            <Skeleton width="12rem" height="2.5rem" />
          </div>
          <Card>
            <div
              css={{
                display: "flex",
                flexDirection: "column",
                gap: "0.5rem"
              }}
            >
              <Skeleton width="100%" height="3rem" />
              {[...Array(5)].map((_, i) => (
                <Skeleton key={i} width="100%" height="3.5rem" />
              ))}
            </div>
          </Card>
        </div>
      )}

      {!loader && (
        <div>
          <Titulo
            botones={
              <Boton callback={() => setSideBar(true)}>
                Nuevo instrumento
              </Boton>
            }
          >
            Instrumental
          </Titulo>

          <Card>
            <TablaFiltrable
              value={instrumental}
              searchFields={SEARCH_FIELDS}
              anchoBusqueda="33%"
              nombreArchivo="instrumental_activo"
              emptyMessage="No hay instrumentos activos"
              onRowClick={onRowClick}
              rows={10}
              css={{
                "& .p-datatable-tbody > tr": { cursor: "pointer" }
              }}
            >
              <Column header="Instrumento" field="instrumento" />
              <Column header="Marca" field="marca" />
              <Column header="Modelo" field="modelo" />
              <Column
                header="Próxima calibración"
                body={(rowData) => {
                  if (!rowData.proximaCalibracion) return null;
                  const vencida = new Date(rowData.proximaCalibracion) < new Date();
                  return (
                    <Tag
                      value={formatFecha(rowData.proximaCalibracion)}
                      severity={vencida ? "danger" : "warning"}
                    />
                  );
                }}
                style={{ width: "180px" }}
              />
            </TablaFiltrable>
          </Card>

          {instrumentalNoActivo.length > 0 && (
            <Accordion
              css={{
                marginTop: "1em",
                "& .p-accordion-header-link": { color: "black" },
                "& .p-toggleable-content": {
                  transition: "max-height 0.15s ease"
                }
              }}
            >
              <AccordionTab
                header={`Instrumentos no activos (${instrumentalNoActivo.length})`}
              >
                <TablaFiltrable
                  value={instrumentalNoActivo}
                  searchFields={SEARCH_FIELDS}
                  anchoBusqueda="33%"
                  nombreArchivo="instrumental_inactivo"
                  emptyMessage="No hay instrumentos inactivos"
                  onRowClick={onRowClick}
                  rows={10}
                  css={{
                    "& .p-datatable-tbody > tr": { cursor: "pointer" }
                  }}
                >
                  <Column header="Instrumento" field="instrumento" />
                  <Column header="Marca" field="marca" />
                  <Column header="Modelo" field="modelo" />
                </TablaFiltrable>
              </AccordionTab>
            </Accordion>
          )}
        </div>
      )}
    </>
  );
}
